import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Lock, At, FlaskConicalIcon } from "@phosphor-icons/react";

const CLINIC_BG = "https://static.prod-images.emergentagent.com/jobs/6095044a-36c4-403a-b71f-9edf76c9066f/images/e45bb62b8ddd0ae5f768c3b9f0bb499eefd2ec5a9ac077c8d48755130ed6626d.png";

export default function Login() {
    const navigate = useNavigate();
    const { loginWithToken } = useAuth();

    const [email, setEmail] = useState("daniel@drdanielquiros.com");
    const [password, setPassword] = useState("");
    const [stage, setStage] = useState("credentials"); // credentials | 2fa_setup | 2fa_required
    const [totpCode, setTotpCode] = useState("");
    const [setupData, setSetupData] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const submit = async (e) => {
        e?.preventDefault();
        setSubmitting(true);
        try {
            if (stage === "credentials") {
                const { data } = await api.post("/auth/login", { email, password });
                if (data.status === "2fa_setup") {
                    setSetupData(data.totp_setup);
                    setStage("2fa_setup");
                    toast.info("Configura 2FA: escanea el QR con Google Authenticator");
                } else if (data.status === "2fa_required") {
                    setStage("2fa_required");
                } else if (data.status === "ok") {
                    loginWithToken(data.token, data.user);
                    navigate("/");
                }
            } else if (stage === "2fa_setup") {
                const { data } = await api.post("/auth/verify-2fa-setup", {
                    email, password, totp_code: totpCode,
                });
                if (data.status === "ok") {
                    loginWithToken(data.token, data.user);
                    toast.success("2FA activado correctamente");
                    navigate("/");
                }
            } else if (stage === "2fa_required") {
                const { data } = await api.post("/auth/login", {
                    email, password, totp_code: totpCode,
                });
                if (data.status === "ok") {
                    loginWithToken(data.token, data.user);
                    navigate("/");
                }
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Error de autenticación");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" data-testid="login-page">
            {/* Left: image / brand */}
            <div className="hidden lg:block relative">
                <img src={CLINIC_BG} alt="" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[var(--ink)]/15"></div>
                <div className="absolute bottom-10 left-10 right-10 text-white">
                    <div className="label-eyebrow text-white/80 mb-3">Suplementos · Dr. Daniel Quirós</div>
                    <h2 className="font-display text-4xl xl:text-5xl leading-[1.05] max-w-md tracking-tight">
                        Formulación médico-estética <em className="text-white/85">basada en evidencia.</em>
                    </h2>
                    <p className="mt-4 text-white/80 max-w-lg text-sm leading-relaxed">
                        Ingesta científica continua de PubMed, Europe PMC y ClinicalTrials.gov. Revisión cada 15 días. Nada inventado, todo trazable.
                    </p>
                </div>
            </div>

            {/* Right: form */}
            <div className="flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="mb-10">
                        <div className="label-eyebrow mb-2">Acceso profesional</div>
                        <h1 className="font-display text-3xl tracking-tight">
                            {stage === "credentials" && "Iniciar sesión"}
                            {stage === "2fa_setup" && "Configurar 2FA"}
                            {stage === "2fa_required" && "Verificación 2FA"}
                        </h1>
                        <p className="text-sm text-[var(--ink-2)] mt-2">
                            {stage === "credentials" && "Sólo personal autorizado de la clínica."}
                            {stage === "2fa_setup" && "Escanea el QR con tu app autenticadora y entra el código de 6 dígitos."}
                            {stage === "2fa_required" && "Introduce el código de 6 dígitos de tu autenticador."}
                        </p>
                    </div>

                    {stage === "2fa_setup" && setupData && (
                        <div className="mb-6 p-5 border hairline bg-white" data-testid="2fa-setup-block">
                            <img src={setupData.qr_data_url} alt="QR 2FA" className="mx-auto w-44 h-44" data-testid="2fa-qr" />
                            <div className="mt-3 text-center">
                                <div className="label-eyebrow mb-1">o introduce el secreto manualmente</div>
                                <code className="mono text-xs break-all text-[var(--ink-2)]">{setupData.secret}</code>
                            </div>
                        </div>
                    )}

                    <form onSubmit={submit} className="space-y-4">
                        {stage === "credentials" && (
                            <>
                                <div>
                                    <label className="label-eyebrow block mb-2">Email</label>
                                    <div className="relative">
                                        <At size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                                        <input
                                            type="email"
                                            required
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            className="w-full pl-9 pr-4 py-3 border hairline bg-white text-sm focus:outline-none focus:border-[var(--sage)]"
                                            data-testid="login-email-input"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="label-eyebrow block mb-2">Contraseña</label>
                                    <div className="relative">
                                        <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                                        <input
                                            type="password"
                                            required
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            className="w-full pl-9 pr-4 py-3 border hairline bg-white text-sm focus:outline-none focus:border-[var(--sage)]"
                                            data-testid="login-password-input"
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        {(stage === "2fa_setup" || stage === "2fa_required") && (
                            <div>
                                <label className="label-eyebrow block mb-2">Código 2FA (6 dígitos)</label>
                                <div className="relative">
                                    <ShieldCheck size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-3)]" />
                                    <input
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]{6}"
                                        maxLength={6}
                                        required
                                        value={totpCode}
                                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                        className="w-full pl-9 pr-4 py-3 border hairline bg-white text-lg mono tracking-[0.4em] focus:outline-none focus:border-[var(--sage)]"
                                        data-testid="login-totp-input"
                                        autoFocus
                                    />
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-primary w-full disabled:opacity-50"
                            data-testid="login-submit-button"
                        >
                            {submitting ? "Verificando…" : stage === "credentials" ? "Continuar" : "Verificar y acceder"}
                        </button>

                        {stage !== "credentials" && (
                            <button
                                type="button"
                                onClick={() => { setStage("credentials"); setTotpCode(""); setSetupData(null); }}
                                className="text-sm text-[var(--ink-2)] hover:text-[var(--ink)] w-full"
                                data-testid="login-back-button"
                            >
                                ← Volver
                            </button>
                        )}
                    </form>

                    <div className="mt-10 text-xs text-[var(--ink-3)] flex items-center gap-2 mono">
                        <ShieldCheck size={14} /> JWT + TOTP · Datos cifrados · uso clínico
                    </div>
                </div>
            </div>
        </div>
    );
}
